import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://nissi-india.com";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const EmployeeReportPrint = () => {
  const q = useQuery();

  const finYearId = localStorage.getItem("finYearId") || "";
  const headerBranchId = localStorage.getItem("branchId") || "";
  const headerBranchType = localStorage.getItem("branchType") || "";

  const empId = q.get("empId") || q.get("employeeId") || ""; // support both

  const [loading, setLoading] = useState(true);
  const [main, setMain] = useState(null);

  const [tables, setTables] = useState({
    history: [],
    grossSalary: [],
    specialHra: [],
    bata: [],
    department: [],
    designation: [],
    empCode: [],
  });

  const axiosCfg = useMemo(
    () => ({
      params: { finYearId: Number(finYearId) },
      headers: {
        "x-finyear-id": Number(finYearId) || "",
        "x-branch-id": headerBranchId ? Number(headerBranchId) : "",
        "x-branch-type": headerBranchType || "",
      },
    }),
    [finYearId, headerBranchId, headerBranchType]
  );

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${BASE_URL}/api/employee-report/print-one/${encodeURIComponent(empId)}`, axiosCfg);

        if (!alive) return;

        if (res?.data?.success) {
          setMain(res.data.main || null);
          setTables(res.data.tables || {});
        } else {
          setMain(null);
          setTables({});
        }

        setLoading(false);

        setTimeout(() => window.print(), 350);
      } catch (e) {
        if (!alive) return;
        setLoading(false);
        setMain(null);
      }
    };

    if (String(empId).trim()) load();
    else setLoading(false);

    return () => {
      alive = false;
    };
  }, [empId, axiosCfg]);

  // ✅ photo path: in ASP it was "~\\Employee_photo\\xxx"
  // In React build, you can serve that folder via Node static:
  // app.use("/Employee_photo", express.static(path.join(__dirname,"../Employee_photo")))
  const photoUrl = useMemo(() => {
    const p = main?.fld_photo || "";
    if (!p) return "";
    // if p already like "\Employee_photo\img.jpg"
    const normalized = p.replace(/\\/g, "/").replace(/^\/+/, "");
    return `${BASE_URL}/${normalized}`;
  }, [main]);

  const val = (x) => (x === null || x === undefined ? "" : String(x));

  return (
    <div className="print-root">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        .print-root { padding: 12px; font-family: Arial, Helvetica, sans-serif; color:#000; }
        .page { border: 1px solid #000; padding: 10px; max-width: 880px; margin: 0 auto; }
        .topHeader { border: 1px solid #000; padding: 10px; display:flex; gap:10px; align-items:center; }
        .logo { width:140px; height:80px; border:1px solid #000; display:flex; align-items:center; justify-content:center; font-weight:700; }
        .headText { flex:1; text-align:center; line-height:1.3; }
        .headText .name { font-size:18px; font-weight:700; }
        .headText .sub { font-size:13px; font-weight:700; }
        .metaGrid { border:1px solid #000; border-top:none; display:grid; grid-template-columns: 1fr 1fr 120px; gap:0; }
        .metaCell { border-right:1px solid #000; padding:8px; font-size:12px; }
        .photoBox { padding:8px; display:flex; align-items:center; justify-content:center; }
        .photo { width:100px; height:110px; object-fit:cover; border:1px solid #000; }
        .sec { border:1px solid #000; border-top:none; }
        .secTitle { padding:6px 8px; font-size:12px; font-weight:700; background:#f3f3f3; border-bottom:1px solid #000; }
        .kv { display:grid; grid-template-columns: 180px 12px 1fr 180px 12px 1fr; gap:0; font-size:12px; }
        .kv div { padding:6px 8px; border-bottom:1px solid #000; }
        .kv .lbl { font-weight:700; }
        .kv .colon { text-align:center; font-weight:700; }
        .footer { border:1px solid #000; border-top:none; padding:6px 8px; font-size:11px; display:flex; justify-content:space-between; }
      `}</style>

      <div className="no-print" style={{ display: "flex", gap: 10, margin: "0 auto 10px", maxWidth: 880 }}>
        <button onClick={() => window.print()} style={{ padding: "8px 14px", fontWeight: 700 }}>Print</button>
        <button onClick={() => window.close()} style={{ padding: "8px 14px", fontWeight: 700 }}>Close</button>
      </div>

      <div className="page">
        <div className="topHeader">
          <div className="logo">LOGO</div>
          <div className="headText">
            <div className="name">Nissi Infotech</div>
            <div className="sub">CIVIL CONSTRUCTION</div>
            <div style={{ fontSize: 12 }}>Employee Report</div>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 20, textAlign: "center" }}>Loading...</div>
        ) : !main ? (
          <div style={{ padding: 20, textAlign: "center" }}>No Records Found</div>
        ) : (
          <>
            {/* Meta + Photo row like screenshot */}
            <div className="metaGrid">
              <div className="metaCell">
                <div><b>Branch</b> : {val(main.fld_branchname)}</div>
                <div style={{ marginTop: 6 }}><b>Email ID</b> : {val(main.fld_emailid)}</div>
                <div style={{ marginTop: 6 }}><b>Mobile Number</b> : {val(main.fld_mobile)}</div>
              </div>

              <div className="metaCell">
                <div><b>Gross Salary</b> : {val(main.fld_grosssalary)}</div>
                <div style={{ marginTop: 6 }}><b>Daily Batta</b> : {val(main.fld_dailybata)}</div>
                <div style={{ marginTop: 6 }}><b>Special HRA</b> : {val(main.fld_SpecialHRA)}</div>
                <div style={{ marginTop: 6 }}><b>Conveyance Allowance</b> : {val(main.fld_lname)}</div>
              </div>

              <div className="photoBox">
                {photoUrl ? (
                  <img className="photo" src={photoUrl} alt="Employee" onError={(e) => (e.currentTarget.style.display = "none")} />
                ) : (
                  <div style={{ border: "1px solid #000", width: 100, height: 110, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                    No Image
                  </div>
                )}
              </div>
            </div>

            {/* BASIC DETAILS */}
            <div className="sec">
              <div className="secTitle">BASIC DETAILS</div>
              <div className="kv">
                <div className="lbl">Employee Name</div><div className="colon">:</div><div>{val(main.fld_employee)}</div>
                <div className="lbl">Employee Code</div><div className="colon">:</div><div>{val(main.fld_Employeecode)}</div>

                <div className="lbl">Blood Group</div><div className="colon">:</div><div>{val(main.fld_bloodgroup)}</div>
                <div className="lbl">Designation</div><div className="colon">:</div><div>{val(main.fld_designationname)}</div>

                <div className="lbl">Department</div><div className="colon">:</div><div>{val(main.fld_departmentname)}</div>
                <div className="lbl">Date of Joining</div><div className="colon">:</div><div>{val(main.DOJ)}</div>

                <div className="lbl">Father Name</div><div className="colon">:</div><div>{val(main.fld_fathername)}</div>
                <div className="lbl">Account Number</div><div className="colon">:</div><div>{val(main.fld_accountno)}</div>

                <div className="lbl">Gender</div><div className="colon">:</div><div>{val(main.fld_gender)}</div>
                <div className="lbl">Bank Name</div><div className="colon">:</div><div>{val(main.fld_bankname)}</div>
              </div>
            </div>

            {/* CONTACT DETAILS */}
            <div className="sec">
              <div className="secTitle">CONTACT DETAILS</div>
              <div className="kv">
                <div className="lbl">Permanent Address</div><div className="colon">:</div><div>{val(main.fld_permaddress)}</div>
                <div className="lbl">Temporary Address</div><div className="colon">:</div><div>{val(main.fld_tempaddress)}</div>

                <div className="lbl">Country</div><div className="colon">:</div><div>{val(main.fld_countryname)}</div>
                <div className="lbl">State</div><div className="colon">:</div><div>{val(main.fld_statename)}</div>

                <div className="lbl">District</div><div className="colon">:</div><div>{val(main.fld_districtname)}</div>
                <div className="lbl">City</div><div className="colon">:</div><div>{val(main.fld_cityname)}</div>
              </div>
            </div>

            {/* PERSONAL DETAILS */}
            <div className="sec">
              <div className="secTitle">PERSONAL DETAILS</div>
              <div className="kv">
                <div className="lbl">Marital Status</div><div className="colon">:</div><div>{val(main.fld_maritalstatus)}</div>
                <div className="lbl">Pre Experience Year(s)</div><div className="colon">:</div><div>{val(main.fld_preexpyrs)}</div>

                <div className="lbl">Date of Birth</div><div className="colon">:</div><div>{val(main.DOB)}</div>
                <div className="lbl">Pre Experience Months</div><div className="colon">:</div><div>{val(main.fld_preexpmonths)}</div>

                <div className="lbl">Qualifications</div><div className="colon">:</div><div>{val(main.fld_qualificationname)}</div>
                <div className="lbl">Year of Completion</div><div className="colon">:</div><div>{val(main.fld_completionyr)}</div>

                <div className="lbl">Driving License Number</div><div className="colon">:</div><div>{val(main.fld_drivinglicnum)}</div>
                <div className="lbl">Driving License Expiry Date</div><div className="colon">:</div><div>{val(main.fld_drivinglicexpdate)}</div>
              </div>
            </div>

            {/* STATUS */}
            <div className="sec">
              <div className="secTitle">STATUS</div>
              <div className="kv">
                <div className="lbl">P.F</div><div className="colon">:</div><div>{val(main.fld_pf)}</div>
                <div className="lbl">P.F Number</div><div className="colon">:</div><div>{val(main.fld_pfno)}</div>

                <div className="lbl">Shift</div><div className="colon">:</div><div>{val(main.fld_shiftname)}</div>
                <div className="lbl">ESI Number</div><div className="colon">:</div><div>{val(main.fld_ESIno)}</div>

                <div className="lbl">E.S.I</div><div className="colon">:</div><div>{val(main.fld_esi)}</div>
                <div className="lbl">Grade</div><div className="colon">:</div><div>{val(main.fld_gradename)}</div>

                <div className="lbl">P.A.N Number</div><div className="colon">:</div><div>{val(main.fld_PANno)}</div>
                <div className="lbl">Salary Basis</div><div className="colon">:</div><div>{val(main.fld_salarybasisname)}</div>

                <div className="lbl">Voter Id</div><div className="colon">:</div><div>{val(main.fld_voterid)}</div>
                <div className="lbl">Aadhar Card Number</div><div className="colon">:</div><div>{val(main.fld_Aadharcardnumber)}</div>

                <div className="lbl">IFSC Code</div><div className="colon">:</div><div>{val(main.fld_IFSCCode)}</div>
                <div className="lbl"></div><div className="colon"></div><div></div>
              </div>
            </div>

            <div className="footer">
              <div>Report Created by Infraconsoft</div>
              <div>1</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeReportPrint;
